import { useCallback, useEffect, useRef, useState } from "react";
import { useViewportFrame } from "../hooks/useViewportFrame";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ApiRequestError, apiFetch } from "../lib/api.js";
import {
  CurationCheckCard,
  CurationVerdictPanel,
  supersessionPillClass,
  type CurationCheck,
} from "../lib/curationCheck.js";
import { proposalOriginLabel, proposedByLabel } from "../lib/proposalOrigin.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import { useConfirm } from "../components/ConfirmDialog.js";
import { MarkdownView, type KnowledgeEntry } from "./TenantAdminTenantKnowledge.js";

// ─────────────────────────────────────────────
// Types (mirror hub serializeProposalSummary — ADR-0131 proposal files)
// ─────────────────────────────────────────────

type ProposalKind = "create" | "edit" | "conflict";

type ProposalSummary = {
  id: string;
  name: string;
  proposal_kind: ProposalKind;
  type: string;
  description: string;
  scope_tags: string[];
  related: string[];
  body: string;
  target_entry: string | null;
  conflict_entries: string[] | null;
  supersedes: string[] | null;
  /** Resolved against the queue: what this replaces, and whether it is still there. */
  supersedes_pending: Array<{ id: string; name: string | null; present: boolean }>;
  /** Pending proposals that claim to replace this one. */
  superseded_by: Array<{ id: string; name: string }>;
  rationale: string;
  origin: string | null;
  /** Machine identifiers — rendered through `proposedByLabel`, never shown raw. */
  proposed_by: string | null;
  updated_by: string;
  /** The person whose Atlas Query session this was extracted from, when it was. */
  session_owner: string | null;
  status: "pending";
  created_at: string;
  updated_at: string;
  /** ADR-0201 §3.4 — the verdict the proposal was written with; null if never checked. */
  check: CurationCheck | null;
};

type ProposalDetail = {
  proposal: ProposalSummary & {
    /** True when the knowledge base moved after this proposal was checked. */
    check_stale?: boolean;
    supersedes_full: Array<{ id: string; name: string | null; description: string | null; body: string | null; present: boolean }>;
    target_entry_full: KnowledgeEntry | null;
    conflict_entries_full: KnowledgeEntry[] | null;
    source: {
      session_id: string | null;
      session_title: string | null;
      messages: Array<{ id: string; role: string; content_excerpt: string }>;
    };
  };
};

/** The reviewed content posted as `final_entry` on accept. */
type FinalEntry = {
  name: string;
  description: string;
  body: string;
  scope_tags: string[];
  related: string[];
};

const KNOWLEDGE_TYPES = [
  "join_path", "column_derivation", "code_definition", "filter_idiom",
  "canonical_query", "metric_definition", "synonym", "temporal_rule", "anti_pattern",
];

const KIND_FILTERS: Array<{ value: "" | ProposalKind; label: string }> = [
  { value: "", label: "All" },
  { value: "create", label: "create" },
  { value: "edit", label: "edit" },
  { value: "conflict", label: "conflict" },
];

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d.toLocaleString() : iso;
}

/**
 * The supersession pill, when a proposal is one end of a replacement pair.
 *
 * Text as well as colour, because colour alone may not carry meaning: the
 * survivor reads plain, the one on its way out reads as a warning.
 */
function supersessionPill(p: { supersedes_pending?: Array<{ present: boolean }>; superseded_by?: Array<unknown> }) {
  if ((p.superseded_by?.length ?? 0) > 0) {
    return <span className={supersessionPillClass("superseded")}> superseded</span>;
  }
  if ((p.supersedes_pending ?? []).some((x) => x.present)) {
    return <span className={supersessionPillClass("supersedes")}> supersedes</span>;
  }
  return null;
}

/**
 * What this proposal replaces, or what replaces it.
 *
 * Rendered before the verdict because it changes what the reviewer is deciding:
 * resolving one end of a pair resolves the other, and a curator who accepts the
 * superseded half has accepted the version that was already corrected.
 */
function SupersessionCard({ detail }: { detail: ProposalDetail["proposal"] }) {
  const replaces = detail.supersedes_full ?? [];
  const replacedBy = detail.superseded_by ?? [];
  if (replaces.length === 0 && replacedBy.length === 0) return null;

  return (
    <div className="card" style={{ display: "grid", gap: 6 }}>
      {replaces.length > 0 && (
        <>
          <div style={{ fontWeight: 600, fontSize: 13 }}>
            Replaces {replaces.length === 1 ? "a proposal" : `${replaces.length} proposals`} waiting for review
          </div>
          {replaces.map((r) =>
            r.present ? (
              <div key={r.id} style={{ fontSize: 13 }}>
                <Link to={`/app/admin/tenant-knowledge/proposal/${r.id}`}>{r.name}</Link>
                {r.description ? <span className="help"> — {r.description}</span> : null}
              </div>
            ) : (
              <div key={r.id} className="help">
                One it replaces is no longer in the queue — it was resolved or withdrawn since.
              </div>
            )
          )}
          <div className="help">Accepting this rejects the ones still listed above.</div>
        </>
      )}
      {replacedBy.length > 0 && (
        <>
          <div style={{ fontWeight: 600, fontSize: 13 }}>
            Replaced by {replacedBy.length === 1 ? "a newer proposal" : `${replacedBy.length} newer proposals`}
          </div>
          {replacedBy.map((r) => (
            <div key={r.id} style={{ fontSize: 13 }}>
              <Link to={`/app/admin/tenant-knowledge/proposal/${r.id}`}>{r.name}</Link>
            </div>
          ))}
          <div className="help">Resolve that one instead — accepting it clears this from the queue.</div>
        </>
      )}
    </div>
  );
}

function kindPillClass(kind: ProposalKind): string {
  if (kind === "edit") return "pill pillWarn";
  if (kind === "conflict") return "pill pillBad";
  return "pill";
}

function typePill(t: string) {
  return <span className="pill">{t.replace(/_/g, " ")}</span>;
}

function toFinalEntry(p: { name: string; description: string; body: string; scope_tags: string[]; related: string[] }): FinalEntry {
  return {
    name: p.name,
    description: p.description,
    body: p.body,
    scope_tags: p.scope_tags ?? [],
    related: p.related ?? [],
  };
}

function entryIsDirty(base: FinalEntry, draft: FinalEntry): boolean {
  return JSON.stringify(base) !== JSON.stringify(draft);
}

// ─────────────────────────────────────────────
// List tab
// ─────────────────────────────────────────────

type Props = {
  /** Called after Bulk-Reject so the parent can refresh the pending badge. */
  onResolved?: () => void;
};

export function TenantKnowledgeProposedTab({ onResolved }: Props) {
  const toast = useToast();
  const { confirm } = useConfirm();
  const navigate = useNavigate();
  const [rows, setRows] = useState<ProposalSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  useViewportFrame(gridRef, { active: !loading && !error });
  const [kindFilter, setKindFilter] = useState<ProposalKind | "">(() => {
    try {
      const v = localStorage.getItem("atlas.tenantKnowledge.proposed.kind");
      if (v === "create" || v === "edit" || v === "conflict") return v;
    } catch { /* ignore */ }
    return "";
  });
  const [typeFilter, setTypeFilter] = useState<string>(() => {
    try { return localStorage.getItem("atlas.tenantKnowledge.proposed.type") ?? ""; } catch { return ""; }
  });

  useEffect(() => {
    try { localStorage.setItem("atlas.tenantKnowledge.proposed.kind", kindFilter); } catch { /* ignore */ }
  }, [kindFilter]);
  useEffect(() => {
    try { localStorage.setItem("atlas.tenantKnowledge.proposed.type", typeFilter); } catch { /* ignore */ }
  }, [typeFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (kindFilter) params.set("proposal_kind", kindFilter);
      if (typeFilter) params.set("knowledge_type", typeFilter);
      const qs = params.toString();
      const res = await apiFetch<{ proposals: ProposalSummary[] }>(
        `/api/tenant/admin/tenant-knowledge-proposals${qs ? `?${qs}` : ""}`
      );
      setRows(res.proposals);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [kindFilter, typeFilter]);

  useEffect(() => { void load(); }, [load]);

  async function bulkReject() {
    if (rows.length === 0) return;
    const ok = await confirm({
      title: `Reject ${rows.length} proposals?`,
      detail: "All proposals currently visible under the active filters will be rejected and removed.",
      confirmLabel: "Reject all"
    });
    if (!ok) return;
    try {
      await apiFetch(`/api/tenant/admin/tenant-knowledge-proposals/bulk-reject`, {
        method: "POST",
        body: { ids: rows.map((r) => r.id) }
      });
      toast.push({ kind: "success", message: "Bulk reject complete." });
      await load();
      onResolved?.();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Bulk reject failed" });
    }
  }

  if (error) return <ErrorState detail={error} />;
  if (loading) return <LoadingState />;

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div className="toolbar">
        <div className="toolbarGroup">
          <label className="field" style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
            <span className="label" style={{ fontSize: 12 }}>Kind:</span>
            <select
              className="select"
              style={{ fontSize: 12 }}
              value={kindFilter}
              onChange={(e) => setKindFilter(e.target.value as ProposalKind | "")}
            >
              {KIND_FILTERS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
            </select>
          </label>
          <label className="field" style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
            <span className="label" style={{ fontSize: 12 }}>Type:</span>
            <select
              className="select"
              style={{ fontSize: 12 }}
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="">All</option>
              {KNOWLEDGE_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
            </select>
          </label>
        </div>
        {rows.length > 0 && (
          <div className="toolbarGroup">
            <button className="btn btnDanger" onClick={() => void bulkReject()}>
              Reject all
            </button>
          </div>
        )}
      </div>

      <div className="tableWrap tableWrapFrame" ref={gridRef}>
        <table className="table">
          <thead>
            <tr>
              <th>Kind</th>
              <th>Proposed by</th>
              <th>Type</th>
              <th>Name</th>
              <th>Summary</th>
              <th>Proposed</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="muted" style={{ textAlign: "center", padding: 24 }}>
                  No proposed changes. Extract knowledge from an Atlas Query session, or wait for the background sweep to surface candidates.
                </td>
              </tr>
            )}
            {rows.map((p) => {
              const name = p.proposal_kind === "conflict"
                ? `${(p.conflict_entries ?? []).length} entries in conflict`
                : p.name;
              return (
                <tr key={p.id} style={{ cursor: "pointer" }} onClick={() => navigate(`/app/admin/tenant-knowledge/proposal/${p.id}`)}>
                  <td>
                    <span className={kindPillClass(p.proposal_kind)}>{p.proposal_kind}</span>
                    {supersessionPill(p)}
                  </td>
                  <td className="muted" style={{ whiteSpace: "nowrap" }}>{proposedByLabel(p.proposed_by, p.updated_by, p.session_owner)}</td>
                  <td>{typePill(p.type)}</td>
                  <td className="mono" style={{ fontSize: 12, whiteSpace: "nowrap" }}>{name}</td>
                  <td className="muted" style={{ fontSize: 12, maxWidth: 420, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={p.description}>{p.description}</td>
                  <td className="muted" style={{ whiteSpace: "nowrap", fontSize: 12 }}>{fmtDateTime(p.created_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Proposal detail page (/app/admin/tenant-knowledge/proposal/:id)
// — same full-page treatment as the entry editor.
// ─────────────────────────────────────────────

export function TenantKnowledgeProposalDetail() {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const { confirm } = useConfirm();
  const navigate = useNavigate();

  const [detail, setDetail] = useState<ProposalDetail["proposal"] | null>(null);
  const [recheckBusy, setRecheckBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);

  // Edit state — draft holds the reviewed content; editing toggles the form.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<FinalEntry | null>(null);
  // Conflict: which entry wins.
  const [keepName, setKeepName] = useState<string>("");
  // ADR-0202 §5.3 — the re-run check's refusal, rendered in place.
  const [acceptBlock, setAcceptBlock] = useState<CurationCheck | null>(null);
  const [acceptOverride, setAcceptOverride] = useState("");
  const [sourceOpen, setSourceOpen] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const res = await apiFetch<ProposalDetail>(`/api/tenant/admin/tenant-knowledge-proposals/${id}`);
        setDetail(res.proposal);
        if (res.proposal.proposal_kind === "conflict") {
          const first = res.proposal.conflict_entries_full?.[0]?.name ?? "";
          setKeepName(first);
          const winner = res.proposal.conflict_entries_full?.find((e) => e.name === first);
          setDraft(winner ? toFinalEntry(winner) : null);
        } else if (res.proposal.proposal_kind === "edit") {
          setDraft(toFinalEntry({ ...res.proposal, name: res.proposal.target_entry ?? res.proposal.name }));
        } else {
          setDraft(toFinalEntry(res.proposal));
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const base: FinalEntry | null = detail
    ? detail.proposal_kind === "conflict"
      ? (() => {
          const winner = detail.conflict_entries_full?.find((e) => e.name === keepName);
          return winner ? toFinalEntry(winner) : null;
        })()
      : detail.proposal_kind === "edit"
        ? toFinalEntry({ ...detail, name: detail.target_entry ?? detail.name })
        : toFinalEntry(detail)
    : null;
  const dirty = base && draft ? entryIsDirty(base, draft) : false;

  function selectWinner(name: string) {
    if (!detail) return;
    setKeepName(name);
    const winner = detail.conflict_entries_full?.find((e) => e.name === name);
    setDraft(winner ? toFinalEntry(winner) : null);
    setEditing(false);
  }

  function cancelEdit() {
    setDraft(base);
    setEditing(false);
  }

  /**
   * ADR-0201 §3.4 — one click fills the final-entry editor with the drafted
   * merge and points it at the target. It writes NOTHING: the curator still
   * reads it and presses Accept.
   */
  function applyMergeDraft() {
    const rec = detail?.check?.recommendation;
    if (!detail || !rec || rec.action !== "merge_into" || !rec.merged_body || !rec.target) return;
    setDraft({
      name: rec.target,
      description: detail.description,
      body: rec.merged_body,
      scope_tags: detail.scope_tags,
      related: detail.related,
    });
    setEditing(true);
    toast.push({ kind: "info", message: `Merge draft loaded into ${rec.target}. Review it, then accept.` });
  }

  async function recheck() {
    if (!detail) return;
    setRecheckBusy(true);
    try {
      const res = await apiFetch<{ proposal: ProposalSummary }>(
        `/api/tenant/admin/tenant-knowledge-proposals/${detail.id}/recheck`,
        { method: "POST" }
      );
      setDetail((d) => (d ? { ...d, check: res.proposal.check } : d));
      toast.push({ kind: "success", message: "Checked again." });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Check failed" });
    } finally {
      setRecheckBusy(false);
    }
  }

  async function accept(overrideReason?: string) {
    if (!detail) return;

    // The gesture resolves records other than the one on screen, so it says so
    // before the click rather than the other rows simply vanishing.
    const alsoRejects = (detail.supersedes_full ?? []).filter((r) => r.present);
    if (alsoRejects.length > 0 && !overrideReason) {
      const ok = await confirm({
        title: "Accept and reject what it replaces?",
        detail: `Accepting this also rejects ${alsoRejects.map((r) => r.name).join(", ")}.`,
        confirmLabel: "Accept"
      });
      if (!ok) return;
    }

    setResolving(true);
    try {
      const body: Record<string, unknown> = {};
      if (detail.proposal_kind === "conflict") {
        body.resolution = { kind: "keep_one", keep_name: keepName };
      }
      if (dirty && draft) body.final_entry = draft;
      if (overrideReason) body.override_reason = overrideReason;
      const res = await apiFetch<{ superseded_rejected?: string[] }>(
        `/api/tenant/admin/tenant-knowledge-proposals/${detail.id}/accept`,
        { method: "POST", body }
      );
      const n = res.superseded_rejected?.length ?? 0;
      toast.push({
        kind: "success",
        message: n > 0 ? `Proposal accepted. ${n} superseded proposal${n === 1 ? "" : "s"} rejected.` : "Proposal accepted."
      });
      navigate("/app/admin/tenant-knowledge");
    } catch (e) {
      // ADR-0202 §5.3 — canon moved since this was checked, and the re-run
      // found a clash. A refusal a curator has to act on cannot live in a
      // toast, so it renders in place with the findings and the way past it.
      if (e instanceof ApiRequestError && e.code === "curation_check") {
        const check = (e.data as { check?: CurationCheck } | undefined)?.check;
        if (check) {
          setAcceptBlock(check);
          return;
        }
      }
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Accept failed" });
    } finally {
      setResolving(false);
    }
  }

  async function reject() {
    if (!detail) return;
    const ok = await confirm({
      title: "Reject proposal?",
      detail: "This proposal will be removed from the queue.",
      confirmLabel: "Reject"
    });
    if (!ok) return;
    setResolving(true);
    try {
      await apiFetch(`/api/tenant/admin/tenant-knowledge-proposals/${detail.id}/reject`, { method: "POST" });
      toast.push({ kind: "success", message: "Proposal rejected." });
      navigate("/app/admin/tenant-knowledge");
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Reject failed" });
    } finally {
      setResolving(false);
    }
  }

  const title = (
    <>
      <Link to="/app/admin/tenant-knowledge">Tenant Knowledge</Link>
      {" / "}
      {detail
        ? detail.proposal_kind === "conflict"
          ? "Conflict review"
          : detail.name
        : "Proposal"}
    </>
  );

  if (error) return <PageLayout title={title}><ErrorState detail={error} /></PageLayout>;
  if (loading || !detail) return <PageLayout title={title}><LoadingState /></PageLayout>;

  const acceptLabel =
    detail.proposal_kind === "conflict"
      ? dirty ? "Keep selected with edits" : "Keep selected"
      : dirty ? "Accept with edits" : "Accept";

  return (
    <PageLayout
      title={title}
      actions={
        editing ? (
          // Edit mode: only editing controls — Accept/Reject return when done.
          <>
            <button className="btn" disabled={resolving} onClick={cancelEdit}>Cancel edits</button>
            <button className="btn btnPrimary" disabled={resolving} onClick={() => setEditing(false)}>Done editing</button>
          </>
        ) : (
          <>
            <button className="btn" disabled={resolving} onClick={() => navigate("/app/admin/tenant-knowledge")}>Cancel</button>
            <button className="btn btnDanger" disabled={resolving} onClick={() => void reject()}>Reject</button>
            <button
              className="btn btnPrimary"
              disabled={resolving || (detail.proposal_kind === "conflict" && !keepName)}
              onClick={() => void accept()}
            >
              {resolving ? "Working…" : acceptLabel}
            </button>
          </>
        )
      }
    >
      <div style={{ display: "grid", gap: 12 }}>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <span className={kindPillClass(detail.proposal_kind)}>{detail.proposal_kind}</span>
          {supersessionPill(detail)}
          {typePill(detail.type)}
          <span className="muted" style={{ fontSize: 12 }}>
            Proposed by {proposedByLabel(detail.proposed_by, detail.updated_by, detail.session_owner)} ·{" "}
            {proposalOriginLabel(detail.origin, new Map())} · {fmtDateTime(detail.created_at)}
          </span>
        </div>

        <SupersessionCard detail={detail} />

        {acceptBlock && (
          <CurationCheckCard
            check={acceptBlock}
            surface="knowledge"
            subject="entry"
            overrideReason={acceptOverride}
            onOverrideReasonChange={setAcceptOverride}
            onCancel={() => {
              setAcceptBlock(null);
              setAcceptOverride("");
            }}
            onOverride={() => {
              const reason = acceptOverride.trim();
              setAcceptBlock(null);
              setAcceptOverride("");
              void accept(reason);
            }}
            busy={resolving}
          />
        )}

        <div className="card" style={{ display: "grid", gap: 6 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>Why this was proposed</div>
          <div style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{detail.rationale}</div>
          {detail.source.messages.length > 0 && (
            <>
              <button
                className="disclosureToggle"
                aria-expanded={sourceOpen}
                onClick={() => setSourceOpen((v) => !v)}
              >
                {sourceOpen ? "▾" : "▸"} Source messages ({detail.source.messages.length})
              </button>
              <div hidden={!sourceOpen} style={{ display: "grid", gap: 4, marginTop: 6 }}>
                {detail.source.messages.map((m) => (
                  <div key={m.id} style={{ fontSize: 12, background: "var(--bg)", borderRadius: 4, padding: "4px 8px" }}>
                    <span className="muted" style={{ fontSize: 10, marginRight: 6 }}>{m.role}:</span>
                    <span style={{ whiteSpace: "pre-wrap" }}>{m.content_excerpt}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* ADR-0201 §3.4 — the review opens with the verdict, above the content. */}
        <CurationVerdictPanel
          check={detail.check}
          surface="knowledge"
          stale={detail.check_stale === true}
          onRecheck={() => void recheck()}
          recheckBusy={recheckBusy}
          onApplyMergeDraft={applyMergeDraft}
        />

        {/* Kind-specific review content */}
        {detail.proposal_kind === "edit" && !editing && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <EntryCard label="Current" entry={detail.target_entry_full ? toFinalEntry(detail.target_entry_full) : null} missingText="Target entry no longer exists." />
            <EntryCard label={dirty ? "Proposed replacement (edited)" : "Proposed replacement"} entry={draft ?? base} />
          </div>
        )}

        {detail.proposal_kind === "create" && !editing && (
          <EntryCard label={dirty ? "Proposed entry (edited)" : "Proposed entry"} entry={draft ?? base} />
        )}

        {detail.proposal_kind === "conflict" && !editing && (
          <div style={{ display: "grid", gap: 8 }}>
            <div className="muted" style={{ fontSize: 12 }}>
              These entries overlap or disagree. Click the one to keep — the others will be archived.
            </div>
            <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(detail.conflict_entries_full?.length ?? 1, 3)}, 1fr)`, gap: 12 }}>
              {(detail.conflict_entries_full ?? []).map((e) => (
                <div
                  key={e.name}
                  onClick={() => selectWinner(e.name)}
                  style={{ cursor: "pointer", borderRadius: 8, outline: e.name === keepName ? "2px solid var(--rasci-r)" : "1px solid var(--border)" }}
                >
                  <EntryCard label={e.name === keepName ? "Keeping this one" : "Will be archived"} entry={toFinalEntry(e)} />
                </div>
              ))}
            </div>
          </div>
        )}

        {editing && draft && (
          <ProposalEditForm
            draft={draft}
            onChange={setDraft}
            nameLocked={detail.proposal_kind !== "create"}
            disabled={resolving}
          />
        )}

        {!editing && (
          <div>
            <button className="btn" style={{ fontSize: 12 }} disabled={!draft} onClick={() => setEditing(true)}>
              {detail.proposal_kind === "conflict" ? "Edit the kept entry before accepting" : "Edit before accepting"}
            </button>
          </div>
        )}
      </div>
    </PageLayout>
  );
}

/** Rendered entry card — the review surface (same markdown treatment as accepted knowledge). */
function EntryCard({ label, entry, missingText }: { label: string; entry: FinalEntry | null; missingText?: string }) {
  return (
    <div className="card" style={{ display: "grid", gap: 6, alignContent: "start" }}>
      <div className="muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
      {entry ? (
        <>
          <div className="mono" style={{ fontWeight: 600, fontSize: 13 }}>{entry.name}</div>
          {entry.description && <div className="muted" style={{ fontSize: 12 }}>{entry.description}</div>}
          <MarkdownView body={entry.body || "*<empty body>*"} />
          {(entry.scope_tags.length > 0 || entry.related.length > 0) && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {entry.scope_tags.map((t) => <span key={`t-${t}`} className="pill" style={{ fontSize: 10 }}>{t}</span>)}
              {entry.related.map((r) => <span key={`r-${r}`} className="pill pillOk" style={{ fontSize: 10 }}>related: {r}</span>)}
            </div>
          )}
        </>
      ) : (
        <div className="muted" style={{ fontSize: 12 }}>{missingText ?? "Not available."}</div>
      )}
    </div>
  );
}

/** Edit form — same layout language as the entry editor (frontmatter card + split body editor). */
function ProposalEditForm({
  draft,
  onChange,
  nameLocked,
  disabled
}: {
  draft: FinalEntry;
  onChange: (next: FinalEntry) => void;
  nameLocked: boolean;
  disabled: boolean;
}) {
  const [scopeTagsText, setScopeTagsText] = useState<string>(() => draft.scope_tags.join(", "));
  const [relatedText, setRelatedText] = useState<string>(() => draft.related.join(", "));

  function parseList(s: string): string[] {
    return s.split(",").map((t) => t.trim()).filter((t) => t.length > 0);
  }

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div className="card" style={{ display: "grid", gap: 10 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 10 }}>
          <label className="field">
            <span className="label">Name</span>
            <input
              className="input mono"
              value={draft.name}
              onChange={(e) => onChange({ ...draft, name: e.target.value })}
              disabled={disabled || nameLocked}
            />
            {nameLocked && <div className="help">This proposal targets an existing entry — the name is fixed.</div>}
          </label>
          <label className="field">
            <span className="label">Description</span>
            <input
              className="input"
              maxLength={200}
              value={draft.description}
              onChange={(e) => onChange({ ...draft, description: e.target.value })}
              disabled={disabled}
            />
          </label>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <label className="field">
            <span className="label">Tags</span>
            <input
              className="input"
              value={scopeTagsText}
              onChange={(e) => {
                setScopeTagsText(e.target.value);
                onChange({ ...draft, scope_tags: parseList(e.target.value) });
              }}
              disabled={disabled}
              placeholder="comma-separated"
            />
          </label>
          <label className="field">
            <span className="label">Related entries</span>
            <input
              className="input"
              value={relatedText}
              onChange={(e) => {
                setRelatedText(e.target.value);
                onChange({ ...draft, related: parseList(e.target.value) });
              }}
              disabled={disabled}
              placeholder="comma-separated names"
            />
          </label>
        </div>
      </div>
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
          <span className="label">Body</span>
          <span className="muted" style={{ fontSize: 12 }}>Markdown — SQL in ```sql fences. Live preview on the right.</span>
        </div>
        <div className="knowledgeEditorSplit">
          <textarea
            className="input"
            value={draft.body}
            onChange={(e) => onChange({ ...draft, body: e.target.value })}
            disabled={disabled}
          />
          <div className="knowledgeMarkdownPane">
            <MarkdownView body={draft.body || "*Nothing to preview yet.*"} />
          </div>
        </div>
      </div>
    </div>
  );
}
